// The player's briefing — ONE modal (index.html #map-intro) for the 2D Map
// ('2d') and the 3D world ('3d'), per device mode ('flat' | 'mobile' | 'vr'):
// what this device can do there, DRAWN (a mouse / phone / controller
// diagram, key caps, pictogram tiles) with a word or two per control.
// APP_STARTUP §7 "The briefing".
//
// Every control drawn here is a binding in the code — player.js FlatControls
// / VRControls, main.js tick + keydown, mobile.js, playermap.js, inspect.js
// map card, stationzones.js sign. A control the code does not have is a bug
// here. No combat rows (owner ruling): click / tap / trigger is the
// monocular's inspect and the station sign, never a gun.
//
// ONE device per page: the phone sees touch / tilt only, VR the Quest
// controllers (+ the controller ray on the flat Map), desktop the mouse +
// keys. No mouse / key word reaches the phone or the headset, no touch word
// the desktop. VR's rows live in vrHands / vrTiles (one place).
//
// It opens by itself the FIRST time per view × mode (`auto`, localStorage
// vrm.brief.<view>.<mode>) and again from a "?" (`open`). It closes on ×,
// OK, a click on the backdrop and Esc. While it is up it owns the input: the
// pointer lock is released (the mouse can reach × / the backdrop), and
// mousedown / wheel / contextmenu / keydown stop at it — nothing behind
// fires, zooms, toggles the Map or walks; keyup passes (no stuck key). A close
// by click never re-captures the mouse: the next click on the view does.

import { t } from './i18n.js';
import './i18n/player.js';

const KEY = (view, mode) => `vrm.brief.${view}.${mode}`;
const C = { cyan: '#67e8f9', amber: '#fbbf24', pink: '#f0abfc', green: '#86efac' };

// ---- pictograms (56×56, stroke = the tile's colour, accents fixed)
const ic = (body) => `<svg class="mi-ic" viewBox="0 0 56 56" aria-hidden="true" fill="none"
  stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
const ICON = {
  drag: ic('<circle cx="28" cy="28" r="5" fill="currentColor"/><path d="M28 6v12M22 12l6-6 6 6M28 50V38M22 44l6 6 6-6M6 28h12M12 22l-6 6 6 6M50 28H38M44 22l6 6-6 6"/>'),
  wheel: ic(`<rect x="10" y="6" width="24" height="40" rx="12"/><path d="M22 6v16"/><rect x="19" y="11" width="6" height="9" rx="3" fill="currentColor"/><path d="M45 12v10M40 17h10M40 38h10" stroke="${C.amber}"/>`),
  card: ic(`<path d="M14 32c-6-7-8-11-8-15a8 8 0 0 1 16 0c0 4-2 8-8 15z"/><circle cx="14" cy="17" r="3"/><rect x="26" y="18" width="26" height="32" rx="4"/><path d="M31 27h16M31 34h11M31 41h14" stroke="${C.amber}"/>`),
  beam: ic(`<path d="M6 38c5-12 10 5 15-6s10 5 15-7"/><path d="M33 17l6 2-2 6"/><ellipse cx="44" cy="44" rx="9" ry="4"/><circle cx="44" cy="33" r="4" fill="${C.amber}" stroke="none"/>`),
  chips: ic(`<rect x="6" y="7" width="44" height="12" rx="6"/><rect x="6" y="22" width="44" height="12" rx="6"/><rect x="6" y="37" width="44" height="12" rx="6"/><circle cx="14" cy="13" r="3" fill="#facc15" stroke="none"/><circle cx="14" cy="28" r="3" fill="#f87171" stroke="none"/><circle cx="14" cy="43" r="3" fill="#60a5fa" stroke="none"/>`),
  people: ic(`<circle cx="14" cy="14" r="6"/><rect x="7" y="23" width="14" height="27" rx="7"/><rect x="9.5" y="30" width="9" height="5" rx="1" fill="${C.amber}" stroke="none"/><circle cx="42" cy="14" r="6"/><rect x="35" y="23" width="14" height="27" rx="7"/><rect x="37.5" y="30" width="9" height="5" rx="1" fill="${C.amber}" stroke="none"/><path d="M24 10a8 8 0 0 1 0 10M27.5 6.5a13 13 0 0 1 0 17" stroke="${C.pink}"/>`),
  follow: ic(`<circle cx="28" cy="28" r="22"/><path d="M28 23l4 9h-8z" fill="currentColor"/><path d="M30 25l10-8" stroke="${C.amber}"/><path d="M35 16l5 1-1 5" stroke="${C.amber}"/><circle cx="43" cy="14" r="4" fill="${C.pink}" stroke="none"/>`),
  jet: ic(`<circle cx="28" cy="9" r="5"/><path d="M28 14v16M18 21l10 3 10-3M28 30l-6 8M28 30l6 8"/><path d="M22 42c0 5 3 7 3 11M34 42c0 5-3 7-3 11M28 41v13" stroke="${C.amber}"/>`),
  bike: ic(`<circle cx="13" cy="38" r="9"/><circle cx="43" cy="38" r="9"/><path d="M13 38l9-15h14l7 15M22 23l7 15h-16M36 23l-3-7h6"/>`),
  lift: ic(`<path d="M4 15L52 7"/><path d="M28 11v8"/><rect x="17" y="19" width="22" height="20" rx="4"/><path d="M17 28h22"/><path d="M6 52h44" stroke-dasharray="3 5"/>`),
  sign: ic(`<rect x="7" y="5" width="42" height="31" rx="3"/><path d="M13 14h22M13 26h16"/><circle cx="42" cy="14" r="3.5" fill="${C.amber}" stroke="none"/><path d="M28 36v14M19 50h18"/>`),
  climb: ic(`<path d="M8 53L34 7" stroke-width="3.2"/><circle cx="37" cy="21" r="4.5"/><path d="M35 26l-5 11M34 28l-8-5M30 37l-6 7M30 37l3 9"/>`),
  capture: ic(`<rect x="13" y="9" width="39" height="33" rx="4"/><path d="M32.5 19v12M26.5 25h12"/><path d="M4 50l9-9M13 41H7M13 41v6" stroke="${C.amber}"/>`),
  esc: ic(`<rect x="5" y="13" width="46" height="30" rx="6"/><path d="M5 37h46" opacity=".5"/><text x="28" y="32" text-anchor="middle" font-size="15" font-weight="800" fill="currentColor" stroke="none" font-family="ui-monospace, monospace">ESC</text>`),
  look: ic('<rect x="21" y="13" width="14" height="30" rx="3"/><path d="M11 18a19 19 0 0 0 0 20M45 18a19 19 0 0 1 0 20M8 33l3 5 4-3M48 33l-3 5-4-3"/>'),
  pinch: ic('<circle cx="21" cy="35" r="5" fill="currentColor"/><circle cx="35" cy="21" r="5" fill="currentColor"/><path d="M15 41l-9 9M6 42v8h8M41 15l9-9M42 6h8v8"/>'),
  tap: ic(`<circle cx="28" cy="28" r="19" stroke-dasharray="4 4"/><circle cx="28" cy="28" r="6" fill="${C.amber}" stroke="none"/>`),
  tilt: ic(`<rect x="5" y="5" width="11" height="20" rx="2.5"/><path d="M21 9a17 17 0 0 1 13 13"/><path d="M30 20l4 3 2-5"/><path d="M8 47l13-11h30L38 47z"/><path d="M19 43l8-3 7 2 8-3" stroke="${C.amber}"/>`),
  gps: ic(`<path d="M28 51C18 39 14 33 14 25a14 14 0 0 1 28 0c0 8-4 14-14 26z"/><circle cx="28" cy="25" r="5" fill="${C.amber}" stroke="none"/>`),
  jump: ic('<path d="M10 50h36"/><path d="M28 40V12M19 21l9-9 9 9"/>'),
  ray: ic(`<path d="M9 47c-3-6 0-12 6-13l7-1 5 6-5 10c-4 3-10 3-13-2z"/><path d="M24 34L49 9" stroke-dasharray="3 4" stroke="${C.amber}"/><circle cx="49" cy="9" r="3.5" fill="${C.amber}" stroke="none"/>`),
  wrist: ic('<path d="M4 50l17-13"/><circle cx="34" cy="25" r="15"/><path d="M34 15v4M34 31v4M24 25h4M40 25h4"/><path d="M34 21l3 7-3-1.5-3 1.5z" fill="currentColor"/>'),
  optic: ic(`<circle cx="17" cy="30" r="11"/><circle cx="39" cy="30" r="11"/><path d="M28 27v6"/><path d="M47 6v10M42 11h10" stroke="${C.amber}"/>`),
  menu: ic('<path d="M10 15h36M10 28h36M10 41h36"/><circle cx="46" cy="41" r="4" fill="currentColor" stroke="none"/>'),
  back: ic('<path d="M22 14L8 28l14 14M8 28h28a12 12 0 0 1 0 24h-6"/>'),
  spot: ic('<circle cx="28" cy="28" r="15" fill="#ef4444" stroke="#fff" stroke-width="4"/><circle cx="28" cy="28" r="22" stroke-dasharray="4 5"/>'),
  zoom: ic(`<circle cx="24" cy="24" r="15"/><path d="M35 35l14 14" stroke-width="4"/><path d="M24 17v14M17 24h14" stroke="${C.amber}"/>`),
};

// ---- the diagrams (labels ≥ 14 px at the drawn size)
const LBL = 'font-family="ui-monospace, SFMono-Regular, Menlo, monospace"';
const mouseSvg = () => `<svg class="mi-hero" viewBox="0 0 400 200" role="img" aria-label="${t('player.brief.mouseAria')}">
  <path d="M157 46C137 46 130 64 130 82v52c0 36 24 54 70 54s70-18 70-54V82c0-18-7-36-27-36z" fill="rgba(103,232,249,.05)" stroke="${C.cyan}" stroke-width="2.5"/>
  <path d="M200 46v56M130 102h140" stroke="${C.cyan}" stroke-width="2.5"/>
  <path d="M157 48c-17 0-24 17-25 34v18h66V48z" fill="rgba(251,191,36,.3)"/>
  <path d="M243 48c17 0 24 17 25 34v18h-66V48z" fill="rgba(34,211,238,.3)"/>
  <rect x="192" y="60" width="16" height="34" rx="8" fill="${C.cyan}"/>
  <g fill="none" stroke-width="1.6"><path d="M150 70l-32-26H8" stroke="${C.amber}"/><path d="M250 70l32-26h110" stroke="${C.cyan}"/>
    <path d="M208 78l70 44h114" stroke="${C.cyan}"/><path d="M130 140l-56 22H8" stroke="#e8ecf4"/></g>
  <g ${LBL} font-weight="700" font-size="17" letter-spacing="1.5"><text x="8" y="36" fill="${C.amber}">${t('player.brief.svgInspect')}</text>
    <text x="392" y="36" text-anchor="end" fill="${C.cyan}">${t('player.brief.svgMap')}</text><text x="392" y="114" text-anchor="end" fill="${C.cyan}">${t('player.brief.svgZoom')}</text>
    <text x="8" y="154" fill="#f1f5fb">${t('player.brief.svgLook')}</text></g>
  <g ${LBL} font-size="14" fill="#dbe5f3"><text x="8" y="64">${t('player.brief.svgMonocular')}</text><text x="392" y="64" text-anchor="end">${t('player.brief.svgMap2d')}</text>
    <text x="392" y="142" text-anchor="end">${t('player.brief.svgInOut')}</text><text x="8" y="182">${t('player.brief.svgMoveMouse')}</text></g>
</svg>`;
const phoneSvg = () => `<svg class="mi-hero" viewBox="0 0 340 196" role="img" aria-label="${t('player.brief.phoneAria')}">
  <rect x="3" y="3" width="334" height="190" rx="22" fill="rgba(103,232,249,.04)" stroke="${C.cyan}" stroke-width="2.5"/>
  <rect x="18" y="12" width="304" height="172" rx="10" fill="rgba(8,12,20,.6)" stroke="rgba(103,232,249,.35)"/>
  <circle cx="42" cy="36" r="13" fill="rgba(250,204,21,.25)" stroke="${C.amber}" stroke-width="2"/><path d="M37 37h10v8H37zM39 37v-3a3 3 0 0 1 6 0v3" fill="none" stroke="#fff" stroke-width="1.6"/>
  <circle cx="292" cy="44" r="23" fill="#e9e4d8" stroke="#fff" stroke-width="2"/><path d="M276 54l10-9 8 4 12-11" stroke="#60a5fa" stroke-width="3" fill="none"/><path d="M292 40l4 8h-8z" fill="#ef4444"/>
  <circle cx="170" cy="86" r="21" fill="none" stroke="#fff" stroke-dasharray="4 4" stroke-width="1.6"/><circle cx="170" cy="86" r="6" fill="${C.amber}"/>
  <circle cx="56" cy="146" r="28" fill="none" stroke="#fff" stroke-opacity=".6" stroke-width="2"/><circle cx="63" cy="139" r="10" fill="rgba(255,255,255,.55)"/>
  <path d="M262 82v88" stroke="${C.cyan}" stroke-width="7" stroke-linecap="round" stroke-opacity=".5"/><circle cx="262" cy="150" r="8" fill="#0a191e" stroke="${C.cyan}" stroke-width="2"/>
  <circle cx="304" cy="104" r="12" fill="rgba(250,204,21,.18)" stroke="rgba(250,204,21,.7)" stroke-width="1.6"/><circle cx="304" cy="148" r="15" fill="rgba(74,222,128,.15)" stroke="rgba(74,222,128,.7)" stroke-width="1.6"/>
  <g ${LBL} font-weight="700" font-size="15" letter-spacing="1"><text x="62" y="34" fill="${C.amber}">${t('player.brief.svgHold')}</text>
    <text x="262" y="40" text-anchor="end" fill="#f1f5fb">${t('player.brief.svgMap')}</text>
    <text x="170" y="130" text-anchor="middle" fill="${C.amber}">${t('player.brief.svgTap')}</text><text x="250" y="96" text-anchor="end" fill="${C.cyan}">${t('player.brief.svgZoom')}</text>
    <text x="92" y="146" fill="#f1f5fb">${t('player.brief.svgWalk')}</text></g>
  <g ${LBL} font-size="14" fill="#dbe5f3"><text x="62" y="52">${t('player.brief.svgTheView')}</text><text x="262" y="58" text-anchor="end">${t('player.brief.svgTapIt')}</text>
    <text x="170" y="147" text-anchor="middle">${t('player.brief.svgInspectLow')}</text>
    <text x="250" y="113" text-anchor="end">${t('player.brief.svgOrPinch')}</text><text x="92" y="164">${t('player.brief.svgTheStick')}</text></g>
</svg>`;
// a Quest-style controller, top view; x = its centre, mirror = the right hand
const ctrl = (x, right, b1, b2) => {
  const s = right ? -1 : 1;
  return `<path d="M${x - 33} 44a33 22 0 0 1 66 0" fill="none" stroke="rgba(232,236,244,.45)" stroke-width="3"/>
  <circle cx="${x}" cy="60" r="27" fill="rgba(103,232,249,.05)" stroke="#e8ecf4" stroke-width="2.2"/>
  <path d="M${x - 15} 84l5 46a10 10 0 0 0 20 0l5-46" fill="rgba(103,232,249,.05)" stroke="#e8ecf4" stroke-width="2.2"/>
  <circle cx="${x - 9 * s}" cy="55" r="9" fill="rgba(103,232,249,.3)" stroke="${C.cyan}" stroke-width="2.4"/>
  <circle cx="${x + 10 * s}" cy="68" r="6" fill="rgba(251,191,36,.3)" stroke="${C.amber}" stroke-width="2"/>
  <circle cx="${x + 14 * s}" cy="52" r="6" fill="rgba(251,191,36,.3)" stroke="${C.amber}" stroke-width="2"/>
  <path d="M${x - 4} 90h8" stroke="${C.pink}" stroke-width="5" stroke-linecap="round"/>
  <path d="M${x + 12 * s} 96v22" stroke="${C.green}" stroke-width="5" stroke-linecap="round"/>
  <g ${LBL} font-weight="800" font-size="11" fill="#fff" text-anchor="middle"><text x="${x + 10 * s}" y="72">${b1}</text><text x="${x + 14 * s}" y="56">${b2}</text></g>`;
};
const ctrlsSvg = () => `<svg class="mi-hero mi-ctrls" viewBox="0 0 340 150" role="img" aria-label="${t('player.brief.ctrlsAria')}">
  ${ctrl(95, false, 'X', 'Y')}${ctrl(245, true, 'A', 'B')}
  <g ${LBL} font-weight="700" font-size="15" letter-spacing="2" fill="#f1f5fb"><text x="24" y="148">${t('player.brief.svgLeft')}</text><text x="316" y="148" text-anchor="end">${t('player.brief.svgRight')}</text></g>
</svg>`;

// ---- building blocks
const tile = (icon, title, sub, tone = '') => `<div class="mi-tile${tone ? ` ${tone}` : ''}">${ICON[icon]}<b>${title}</b>${sub ? `<span>${sub}</span>` : ''}</div>`;
const tiles = (list) => `<div class="mi-tiles">${list.filter(Boolean).join('')}</div>`;
const k = (...keys) => keys.map((x) => `<kbd>${x}</kbd>`).join('');
const keyRow = (list) => `<div class="mi-keys">${list.filter(Boolean).map(([keys, what]) => `<span class="mi-k">${keys} ${what}</span>`).join('')}</div>`;
const sec = (h) => `<div class="mi-sec">${h}</div>`;
const sw = (c) => `<i class="mi-sw" style="background:${c}"></i>`;
const legend = (cols) => `<div class="mi-legend">${cols.map(([h, rows]) => `<div><div class="mi-lh">${h}</div><ul>${rows.filter(Boolean).map(([c, b, w]) => `<li>${sw(c)}<b>${b}</b> ${w}</li>`).join('')}</ul></div>`).join('')}</div>`;

// the content: (view, mode, s) → { title, html }. s = { pre (the Map before
// entry), voice (the mic is primed: M / VR Y mute), others (the Map
// shows the other players: combat off), wrist (VR's wrist map: combat off),
// spots (the world's red beam spots are on the Map: PlayerMap.spotsOn) }
function content(view, mode, s) {
  const act = mode === 'mobile' ? t('player.brief.actTap') : mode === 'vr' ? t('player.brief.actVr') : t('player.brief.actClick');
  const people = tile('people', t('player.brief.othersTitle'), t('player.brief.othersSub') + (s.voice ? ` · ${t('player.brief.othersVoice')}` : ` · ${t('player.brief.othersNoVoice')}`), 'pink');
  if (view === '2d') {
    const lead = s.pre
      ? `<div class="mi-lead">${t('player.brief.leadPre', { act })}</div>`
      : `<div class="mi-lead">${t('player.brief.leadIn', { act })}</div>`;
    // per device: the phone's fingers, the Quest browser's controller ray
    // (the flat page: + − zoom, no wheel), the desktop's mouse
    const map = mode === 'mobile' ? [
      tile('drag', t('player.brief.drag'), t('player.brief.dragSub')),
      tile('pinch', t('player.brief.pinch'), t('player.brief.zoomOrPm')),
      tile('card', t('player.brief.tapSpot'), t('player.brief.cardSub'), 'amber'),
      tile('beam', '⤳ Beam', s.pre ? t('player.brief.beamPre') : t('player.brief.beamGo'), 'amber'),
      s.spots ? tile('spot', t('player.brief.tapRed'), s.pre ? t('player.brief.redPre') : t('player.brief.redGo'), 'amber') : null,
      tile('chips', '🚵 🚰', t('player.brief.chipsSub')),
      s.others ? tile('follow', t('player.brief.players'), s.pre ? t('player.brief.playersDots') : t('player.brief.playersTap'), 'pink') : null,
      !s.pre ? tile('back', '×', t('player.brief.back3d')) : null,
    ] : mode === 'vr' ? [
      tile('ray', t('player.brief.vrPoint'), t('player.brief.vrPointSub')),
      tile('drag', t('player.brief.vrHold'), t('player.brief.dragSub')),
      tile('zoom', '+ −', t('player.brief.zoom')),
      tile('card', t('player.brief.vrTrigSpot'), t('player.brief.cardSub'), 'amber'),
      tile('beam', '⤳ Beam', s.pre ? t('player.brief.beamVrPre') : t('player.brief.beamGo'), 'amber'),
      s.spots ? tile('spot', t('player.brief.vrRed'), s.pre ? t('player.brief.redVrPre') : t('player.brief.redGo'), 'amber') : null,
      tile('chips', '🚵 🚰', t('player.brief.chipsSub')),
      s.others ? tile('follow', t('player.brief.players'), t('player.brief.playersDots'), 'pink') : null,
    ] : [
      tile('drag', t('player.brief.drag'), t('player.brief.dragSub')),
      tile('wheel', t('player.brief.wheel'), t('player.brief.zoomOrPm')),
      tile('card', t('player.brief.clickSpot'), t('player.brief.cardSub'), 'amber'),
      tile('beam', '⤳ Beam', s.pre ? t('player.brief.beamPre') : t('player.brief.beamGo'), 'amber'),
      s.spots ? tile('spot', t('player.brief.clickRed'), s.pre ? t('player.brief.redPre') : t('player.brief.redGo'), 'amber') : null,
      tile('chips', '🚵 🚰', t('player.brief.chipsSub')),
      s.others ? tile('follow', t('player.brief.players'), s.pre ? t('player.brief.playersDots') : t('player.brief.playersClick'), 'pink') : null,
      !s.pre ? tile('back', `${k('RMB')} ${k('N')} ${k('Esc')} ×`, t('player.brief.back3d')) : null,
    ];
    let html = lead + tiles(map);
    if (mode === 'vr') html += sec(t('player.brief.inHeadset')) + vrWorld(s, people);
    return { title: t('player.brief.theMap'), html };
  }
  if (mode === 'vr') return { title: t('player.brief.inHeadset'), html: vrWorld(s, people) };
  if (mode === 'mobile') {
    return { title: t('player.brief.inWorld'), html: phoneSvg() + tiles([
      tile('look', t('player.brief.pointPhone'), t('player.brief.pointPhoneSub')),
      tile('tap', t('player.brief.tapCentre'), t('player.brief.tapCentreSub'), 'amber'),
      tile('pinch', t('player.brief.pinchSlider'), t('player.brief.pinchSliderSub')),
      tile('tilt', t('player.brief.tipFlat'), t('player.brief.tipFlatSub')),
      tile('jump', t('player.brief.jump'), t('player.brief.jumpSub')),
      tile('jet', t('player.brief.jet'), t('player.brief.jetSub')),
      tile('gps', '📍 GPS', t('player.brief.gpsSub')),
      people,
    ]) };
  }
  // desktop
  return { title: t('player.brief.inWorld'), html: tiles([
    tile('capture', t('player.brief.clickView'), t('player.brief.clickViewSub'), 'amber'),
    tile('esc', `${k('Esc')}`, t('player.brief.escSub'), 'amber'),
  ]) + mouseSvg() + keyRow([
    [k('W', 'A', 'S', 'D'), t('player.brief.keyMove')], [k('Shift'), t('player.brief.keySprint')], [k('Space'), t('player.brief.keyJump')],
    [k('E'), t('player.brief.keyClimb')], [k('B'), t('player.brief.keyBike')], [k('N'), t('player.brief.keyMap')], [k('F', 'G'), t('player.brief.keySign')],
    s.voice ? [k('M'), t('player.brief.keyMic')] : null, [k('?'), t('player.brief.keyHelp')],
  ]) + tiles([
    tile('jet', t('player.brief.jet'), t('player.brief.jetDesk', { s: k('S') })),
    tile('bike', `${k('B')} Bike`, t('player.brief.bikeSub', { w: k('W'), s: k('S'), a: k('A'), d: k('D'), q: k('Q') })),
    tile('sign', t('player.brief.lifts'), t('player.brief.liftsDesk')),
    people,
  ]) };
}
// ---- VR: what the headset does (Quest controllers only). Shown on the Map
// before the goggles go on ('2d' × 'vr', under "In the headset") and as the
// '3d' × 'vr' page. A new VR binding = one row in vrHands (a button / stick /
// trigger / grip of one hand: [colour, control, what]) or one tile in vrTiles
// (a gesture or a place in the headset); nothing else changes.
function vrHands(s) {
  return [
    [t('player.brief.handLeft'), [
      [C.cyan, t('player.brief.cStick'), t('player.brief.lStick')],
      [C.cyan, t('player.brief.cStickClick'), t('player.brief.lStickClick')],
      [C.amber, 'X', t('player.brief.lX')],
      [C.amber, 'Y', `${s.voice ? `${t('player.brief.lYMic')} · ` : ''}${t('player.brief.lY')}`],
      [C.pink, t('player.brief.cTrigger'), t('player.brief.lTrigger')],
      [C.green, t('player.brief.cGrip'), t('player.brief.lGrip')],
    ]],
    [t('player.brief.handRight'), [
      [C.cyan, t('player.brief.cStickUD'), t('player.brief.rStickUD')],
      [C.cyan, t('player.brief.cStickLR'), t('player.brief.rStickLR')],
      [C.cyan, t('player.brief.cStickClick'), t('player.brief.rStickClick')],
      [C.amber, 'A', t('player.brief.rA')],
      [C.amber, 'B', t('player.brief.rB')],
      [C.pink, t('player.brief.cTrigger'), t('player.brief.rTrigger')],
      [C.green, t('player.brief.cGrip'), t('player.brief.rGrip')],
    ]],
  ];
}
function vrTiles(s, people) {
  return [
    tile('ray', t('player.brief.tFinger'), t('player.brief.tFingerSub')),
    tile('optic', 'HUD', t('player.brief.tHudSub')),
    s.wrist ? tile('wrist', t('player.brief.tWrist'), t('player.brief.tWristMap')) : null,
    tile('menu', t('player.brief.tMenu'), t('player.brief.tMenuSub')),
    tile('beam', t('player.brief.tMapPanel'), t('player.brief.tMapPanelSub'), 'amber'),
    s.vrSpots ? tile('spot', t('player.brief.tMapRed'), t('player.brief.tMapRedSub'), 'amber') : null,
    tile('lift', t('player.brief.lifts'), t('player.brief.liftsVr')),
    people,
  ];
}
function vrWorld(s, people) {
  return ctrlsSvg() + legend(vrHands(s)) + tiles(vrTiles(s, people))
    + `<div class="mi-note">${t('player.brief.vrEnd')}</div>`;
}

export class Briefing {
  // el = #map-intro. o.state() → { pre, voice, others, wrist, spots, vrSpots } (main.js,
  // read at every open); o.status() → the phone's outcome lines (gyro, wake,
  // GPS), o.gpsBeam() → show "📍 Beam to my position"
  constructor(el, o = {}) {
    this.el = el;
    this.o = o;
    this.view = null;
    this.mode = null;
    this.onClose = null;
    if (!el) return;
    this._body = el.querySelector('#mi-content');
    this._title = el.querySelector('#mi-title');
    this._again = el.querySelector('#mi-again');
    this._status = el.querySelector('#mi-status');
    this._gps = el.querySelector('#mi-gps');
    const x = el.querySelector('#mi-x'), ok = el.querySelector('#mi-ok');
    if (x) x.addEventListener('click', () => this.close());
    if (ok) ok.addEventListener('click', () => this.close());
    // the backdrop: a whole click (down + up) outside the box closes — the
    // press and the release both land here, so nothing under it sees either
    el.addEventListener('click', (e) => { if (e.target === el) this.close(); });
    // nothing behind the modal: no fire / RMB Map toggle (FlatControls'
    // document mousedown), no view zoom (main.js document wheel), no menu
    for (const t of ['mousedown', 'pointerdown', 'wheel', 'contextmenu']) {
      el.addEventListener(t, (e) => {
        e.stopPropagation();
        if (t === 'contextmenu') e.preventDefault();
      }, { passive: t !== 'contextmenu' });
    }
    // keys: Esc closes; every other keydown stops here (FlatControls and the
    // global shortcuts never see it); keyup passes, so a held key releases
    addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      e.stopImmediatePropagation();
      if (e.code === 'Escape') { e.preventDefault(); this.close(); }
    }, true);
  }

  get isOpen() { return !!this.el && !this.el.hidden; }

  // the first time this view × mode is shown: open, and remember it
  auto(view, mode) {
    if (!this.el || !view || !mode) return false;
    let seen = false;
    try { seen = localStorage.getItem(KEY(view, mode)) === '1'; } catch { /* private mode: show */ }
    if (seen) return false;
    this.open(view, mode);
    return true;
  }

  open(view, mode) {
    if (!this.el || !view || !mode) return;
    try { localStorage.setItem(KEY(view, mode), '1'); } catch { /* quota / private */ }
    this.view = view; this.mode = mode;
    const s = { pre: false, voice: false, others: true, wrist: true, ...(this.o.state ? this.o.state() : {}) };
    const c = content(view, mode, s);
    this.el.dataset.view = view;
    this.el.dataset.mode = mode;
    this._title.textContent = `${c.title} · ${mode === 'mobile' ? t('player.brief.devPhone') : mode === 'vr' ? 'VR' : t('player.brief.devDesktop')}`;
    this._body.innerHTML = c.html;
    if (this._again) {
      // where THIS device's "?" is (the headset has none: its page is read
      // on the Map, whose top bar has one)
      this._again.textContent = view === '2d' ? t('player.brief.againBar')
        : mode === 'flat' ? t('player.brief.againFlat')
          : mode === 'mobile' ? t('player.brief.againMobile') : '';
    }
    this.refresh();
    // the mouse must reach × and the backdrop
    if (document.pointerLockElement) document.exitPointerLock();
    this.el.hidden = false;
  }

  // the live parts: the phone's outcome lines + the 📍 button
  refresh() {
    if (!this.el) return;
    if (this._status) {
      const lines = this.mode === 'mobile' && this.o.status ? this.o.status() : [];
      this._status.replaceChildren(...lines.map((t) => {
        const d = document.createElement('div');
        d.textContent = t;
        return d;
      }));
      this._status.hidden = !lines.length;
    }
    if (this._gps) {
      const on = this.view === '2d' && this.mode === 'mobile' && !!(this.o.gpsBeam && this.o.gpsBeam());
      if (on && this._gps.hidden) this._gps.textContent = t('player.brief.gpsBeam');
      this._gps.hidden = !on;
    }
  }

  close() {
    if (!this.isOpen) return;
    this.el.hidden = true;
    if (this.onClose) this.onClose(this.view, this.mode);
  }
}
